import { getSharingTokenForJournal } from 'core/api/journals'
import './ShareJournalDialog.scss'

import { IconsSrc } from 'core/assets'
import { Contact, JournalDetailedInfo, JournalInfo } from 'core/types'
import { useCallback, useState } from 'react'
import { Button } from 'shared/Button'
import { Modal, ModalController, useModal } from 'shared/Modal'
import { ModalLayout } from 'shared/ModalLayout'
import { useNotifications } from 'shared/Notifications'

type ShareJournalDialogProps = {
  controller: ModalController<any>
  journal?: JournalInfo | JournalDetailedInfo | null
  isLoading: boolean
  onRevokeAccess: (email: string) => Promise<any>
  onShare: (email: string) => Promise<void>
}
export function ShareJournalDialog({
  controller,
  journal,
  isLoading,
  onRevokeAccess,
  onShare,
}: Readonly<ShareJournalDialogProps>) {
  const [userString, setUserString] = useState('')
  const { showNotification } = useNotifications()

  const confirmRevoke = useModal<boolean>()
  const openConfirmRevokeModal = async (email: string) => {
    const shouldRevoke = await confirmRevoke.open()
    if (shouldRevoke) {
      try {
        await onRevokeAccess(email)
        confirmRevoke.close()
      } catch (err) {}
    }
  }
  const shareJournal = async () => {
    if (!journal) return
    try {
      await onShare(userString)
      setUserString('')
    } catch (err) {}
  }

  const [loading, setLoading] = useState(false)
  const copyLink = useCallback(async () => {
    setLoading(true)
    try {
      const response = await getSharingTokenForJournal(journal?.id ?? '')
      const token = response.data.result

      const newUrl = new URL(window.location.origin + '/home/dashboard')
      newUrl.searchParams.set('sharedJournalId', journal?.id ?? '')
      newUrl.searchParams.set('sharingToken', token)

      await navigator.clipboard.writeText(newUrl.toString())
      showNotification({ text: 'Link copied', type: 'success' })
    } catch (err) {
      showNotification({ text: 'Failed to copy link', type: 'error' })
    } finally {
      setLoading(false)
    }
  }, [showNotification, journal])

  return (
    <>
      <ModalLayout className="ShareJournalDialog">
        <ModalLayout.CloseButton onClick={() => controller.close()} />
        <ModalLayout.Heading>
          Share {journal?.journalName ? `"${journal?.journalName}"` : 'journal'}
        </ModalLayout.Heading>

        {/* <div className="hint text-description-regular color-secondary-l3">
          Anyone with the link can view the journal
        </div> */}

        <ModalLayout.FormControl label="Email">
          <input
            type="text"
            placeholder="example@email.com"
            value={userString}
            onChange={(e) => setUserString(e.target.value)}
          />
          <Button
            color="secondary"
            isLoading={isLoading}
            disabled={userString.trim() === ''}
            onClick={() => shareJournal()}
          >
            <img src={IconsSrc.Share} alt="Share" className="icon" />
            Share
          </Button>
          <div className="hint text-description-regular color-secondary-l3">
            The journal can only be viewed by users who has access
          </div>
        </ModalLayout.FormControl>

        {/* <ModalLayout.Description>
          Share this journal with other users by copying the link. Anyone with the link can view the
          journal
        </ModalLayout.Description> */}

        <div className="text-normal-bold">Link</div>
        <Button
          className="copy-link"
          color="primary"
          onClick={() => copyLink()}
          isLoading={loading}
        >
          <img src={IconsSrc.Link} alt="Link" className="icon" />
          Copy link
        </Button>
        <div className="hint text-description-regular color-secondary-l3">
          Anyone with the link can view the journal
        </div>

        {journal && (
          <AccessControlBlock journal={journal} onRevokeAccess={openConfirmRevokeModal} />
        )}
      </ModalLayout>
      <Modal controller={confirmRevoke}>
        <ModalLayout>
          <ModalLayout.CloseButton onClick={() => confirmRevoke.close()} />
          <ModalLayout.Heading>Revoke access</ModalLayout.Heading>
          <ModalLayout.Description>
            Are you sure you want to revoke access to the journal for this user?
          </ModalLayout.Description>
          <ModalLayout.Buttons>
            <Button onClick={() => confirmRevoke.close(false)}>Cancel</Button>
            <Button
              color="error"
              onClick={() => confirmRevoke.resolveWithoutClosing(true)}
              isLoading={isLoading}
            >
              Revoke
            </Button>
          </ModalLayout.Buttons>
        </ModalLayout>
      </Modal>
    </>
  )
}

type AccessControlBlockProps = {
  journal: JournalInfo | JournalDetailedInfo
  onRevokeAccess: (email: string) => void
}
function AccessControlBlock({ journal, onRevokeAccess }: Readonly<AccessControlBlockProps>) {
  const sharedUsers = journal.sharedWith || []

  return (
    <div className="AccessControlBlock">
      <div className="text-big-bold">Who has access</div>
      <div className="list">
        <AccessControlEntry isOwner contact={journal.owner} />
        {sharedUsers.map((contact) => (
          <AccessControlEntry
            key={contact.userEmail}
            contact={contact}
            onRevokeAccess={() => onRevokeAccess(contact.userEmail)}
          />
        ))}
      </div>
    </div>
  )
}

type AccessControlEntryProps = {
  isOwner?: boolean
  contact: Contact
  onRevokeAccess?: () => void
}
function AccessControlEntry({
  isOwner,
  contact,
  onRevokeAccess,
}: Readonly<AccessControlEntryProps>) {
  return (
    <div className="AccessControlEntry">
      <div className="contact-info">{contact.userEmail}</div>
      {isOwner ? (
        <Button appearance="text" size="small" disabled>
          Owner
        </Button>
      ) : (
        <Button
          size="small"
          onClick={() => onRevokeAccess && onRevokeAccess()}
          appearance="link"
          color="error"
        >
          Revoke access
        </Button>
      )}
    </div>
  )
}
